import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { playSound } from '../../audio/sounds.js';
import { GameOverContext } from '../common/TurnStatus.js';
import { TetraxField } from './TetraxBoard.js';
import { TetraxStage } from './TetraxStage.js';
import type { ArenaSocket } from '../../realtime.js';
import type { ArenaResult } from '../../../shared/protocol.js';
import { newGame, receiveGarbage, type TetraxGameView, type TetraxState } from '../../../shared/games/tetrax/index.js';

/** The board is sent at most this often while the piece moves (always at once after a lock). */
const SEND_MS = 150;

/** State of the running duel: pushed by the server, re-requested after reconnecting. */
export function useTetraxState(socket: ArenaSocket | null, gameId: number, enabled: boolean): { state: TetraxGameView | null; offset: number } {
  const [state, setState] = useState<TetraxGameView | null>(null);
  const offset = useRef(0);
  useEffect(() => {
    if (!socket || !enabled) return;
    const onState = (s: TetraxGameView) => {
      offset.current = s.serverNow - Date.now();
      setState(s);
    };
    const sync = () =>
      socket.emit('tetrax:sync', (r: ArenaResult<{ state: TetraxGameView | null }>) => {
        if (r.ok && r.state) onState(r.state);
      });
    socket.on('tetrax:state', onState);
    socket.on('connect', sync);
    if (socket.connected) sync();
    return () => {
      socket.off('tetrax:state', onState);
      socket.off('connect', sync);
    };
  }, [socket, gameId, enabled]);
  return { state: state && state.id === gameId ? state : null, offset: offset.current };
}

const clock = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * A Tetrax duel (user request 2026-10-03): the own board runs here (the moves answer at once) from
 * the shared seed; it is sent to the server, which passes it to the opponent and sends the garbage.
 * After a reload the board continues from the copy the server has.
 */
export function TetraxGame(props: { state: TetraxGameView; offset: number; socket: ArenaSocket }) {
  const { state, socket } = props;
  const over = useContext(GameOverContext);
  const me = state.players[state.mySeat]!;
  const opponent = state.players[1 - state.mySeat]!;
  const board = useRef<TetraxState>(me.state ?? newGame(state.seed));
  const [, setNow] = useState(0);
  const serverNow = Date.now() + props.offset;
  // From the clock: the server sends nothing at the start when nobody's board has changed yet.
  const started = serverNow >= state.startsAt;
  const playing = !over && state.phase !== 'over' && started && !board.current.over;

  // The board is sent after a change (a lock at once, moves at most every SEND_MS).
  const dirty = useRef(false);
  const lastSent = useRef(0);
  const send = () => {
    dirty.current = false;
    lastSent.current = Date.now();
    socket.emit('tetrax:update', { state: board.current });
  };
  const onChange = () => {
    dirty.current = true;
  };
  useEffect(() => {
    if (!playing) return;
    // The opponent sees the board from the start.
    send();
    const timer = setInterval(() => dirty.current && Date.now() - lastSent.current >= SEND_MS && send(), 50);
    return () => clearInterval(timer);
  }, [playing]);

  // Garbage from the opponent.
  useEffect(() => {
    if (receiveGarbage(board.current, state.garbage) > 0) send();
  }, [state.garbage.length]);

  // The countdown and the clock.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, []);
  const countdown = started ? 0 : Math.ceil((state.startsAt - serverNow) / 1000);
  const lastBeep = useRef(0);
  useEffect(() => {
    if (countdown > 0 && countdown !== lastBeep.current) {
      lastBeep.current = countdown;
      playSound('snakeCount');
    }
  }, [countdown]);

  const overlay =
    countdown > 0 ? (
      <div class="tetrax-big">{countdown}</div>
    ) : !started ? (
      <div class="tetrax-big">{t.tetrax.go}</div>
    ) : board.current.over && !over ? (
      <div class="tetrax-big tetrax-small">{t.tetrax.toppedOut}</div>
    ) : null;

  return (
    <div class="tetrax">
      <div class="tetrax-hud">
        <span>
          {t.tetrax.you}: <strong>{board.current.score.toLocaleString('fi-FI')}</strong>
        </span>
        <span class="tetrax-clock" aria-label={t.tetrax.timeLeft}>
          ⏱ {clock(started ? state.endsAt - serverNow : state.endsAt - state.startsAt)}
        </span>
        <span>
          {opponent.displayName}: <strong>{(opponent.state?.score ?? 0).toLocaleString('fi-FI')}</strong>
          {!opponent.online && <span class="muted"> ({t.tetrax.offline})</span>}
        </span>
      </div>
      <TetraxStage
        state={board}
        active={playing}
        dim={!playing}
        overlay={overlay}
        showGarbage
        onChange={onChange}
        onEvents={send}
        side={
          <div class="tetrax-opponent">
            <div class="tetrax-label">{opponent.displayName}</div>
            {opponent.state ? (
              <TetraxField state={opponent.state} cell={14} version={0} ghost={false} dim={opponent.state.over} label={t.tetrax.opponentBoard(opponent.displayName)} />
            ) : (
              <div class="tetrax-waiting muted">{t.tetrax.waiting}</div>
            )}
            {opponent.state && (
              <div class="tetrax-opponent-stats muted">
                {t.tetrax.lines} {opponent.state.lines} · {t.tetrax.level} {opponent.state.level}
              </div>
            )}
          </div>
        }
      />
      <p class="tetrax-keys muted">{t.tetrax.keys}</p>
    </div>
  );
}
